import _typeof from '@babel/runtime/helpers/typeof';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _PropTypes from 'prop-types';
import * as React from 'react';
import styled from 'styled-components';
import { useThemeContext } from '../../styles/themes/themes.js';
import { useTranslations } from '../../hooks/use_translations.js';
import { spacing4x, spacing2x } from '../../styles/spacing.js';
import { getAdornment } from '../../utils/adornment.js';
import { IconClose } from '../Icons/IconClose.js';
import { LinkButton } from '../LinkButton/LinkButton.js';
import { jsxs, jsx } from 'react/jsx-runtime';

var _excluded = ["children", "value", "onRemove", "startAdornment", "variant"];
var StyledTag = styled.span.withConfig({
  displayName: "Tag__StyledTag",
  componentId: "sc-16x02k3-0"
})(["&&{display:inline-flex;border-radius:4px;min-width:24px;max-width:200px;overflow:hidden;padding:0 8px;background:", ";color:", ";line-height:24px;height:24px;font-weight:600;font-size:12px;align-items:center;", ";", ";", ";", ";}&& svg{display:inline-flex;height:", ";width:", ";vertical-align:text-top;}"], function (props) {
  return props.theme.neutralColors.silver;
}, function (props) {
  return props.theme.neutralColors.charcoal;
}, function (props) {
  switch (props.variant) {
    case "error":
      return "background-color: #FAD2D6";

    case "info":
      return "background-color: #D1E8FD";

    case "success":
      return "background-color: #CCE8D2";

    case "warning":
      return "background-color: #FFF3CD";
  }
}, function (props) {
  return props.error && "background-color: #FAD2D6";
}, function (props) {
  return props.success && "background-color: #CCE8D2";
}, function (props) {
  return props._disabled && "background-color: ".concat(props.theme.neutralColors.pearl, ";\n        color: ").concat(props.theme.neutralColors.rhino, ";\n    ");
}, spacing4x, spacing4x);
var TagDescription = styled.span.withConfig({
  displayName: "Tag__TagDescription",
  componentId: "sc-16x02k3-1"
})(["&&{text-overflow:ellipsis;overflow:hidden;white-space:nowrap;", ";", ";}"], function (props) {
  return props.pushRight && "margin-inline-end: ".concat(spacing2x, ";");
}, function (props) {
  return props.pushLeft && "margin-inline-start: ".concat(spacing2x, ";");
});
var StyledLinkButton = styled(LinkButton).withConfig({
  displayName: "Tag__StyledLinkButton",
  componentId: "sc-16x02k3-2"
})(["&&{color:", " !important;}"], function (props) {
  return props.theme.neutralColors.smoke;
});
var Tag = /*#__PURE__*/React.forwardRef(function Tag2(props, ref) {
  var _useThemeContext = useThemeContext(),
      localization = _useThemeContext.localization;

  var _useTranslations = useTranslations(localization === null || localization === void 0 ? void 0 : localization.translations),
      t = _useTranslations.t;

  var children = props.children,
      value = props.value,
      onRemove = props.onRemove,
      startAdornment = props.startAdornment,
      _props$variant = props.variant,
      variant = _props$variant === void 0 ? "default" : _props$variant,
      rest = _objectWithoutProperties(props, _excluded);

  var styleModifiers = {
    _disabled: props.disabled,
    pushLeft: !!props.startAdornment && !!props.value,
    pushRight: !!props.onRemove && !!props.value
  };
  return /*#__PURE__*/jsxs(StyledTag, _objectSpread(_objectSpread(_objectSpread({}, styleModifiers), {}, {
    "aria-disabled": props.disabled
  }, rest), {}, {
    variant: variant,
    ref: ref,
    children: [startAdornment && /*#__PURE__*/jsx("span", {
      children: getAdornment(startAdornment)
    }), children !== null && children !== void 0 ? children : /*#__PURE__*/jsx(TagDescription, _objectSpread(_objectSpread({}, styleModifiers), {}, {
      children: value
    })), onRemove && /*#__PURE__*/jsx(StyledLinkButton, {
      disabled: props.disabled,
      hideLabel: true,
      label: t("Tag.removeTag"),
      onClick: onRemove,
      startAdornment: IconClose,
      style: {
        alignSelf: "center",
        display: "flex"
      }
    })]
  }));
});
Tag.propTypes = {
  "children": _PropTypes.node,
  "disabled": _PropTypes.bool,
  "onRemove": _PropTypes.func,
  "startAdornment": function checkAdornment(props, propName, componentName) {
    var isReactNodeType = function isReactNodeType(el) {
      var REACT_ELEMENT_TYPE = typeof Symbol === 'function' && Symbol.for ? Symbol.for('react.element') : 0xeac7;
      var isValidElement = _typeof(el) === 'object' && el !== null && el['\$\$typeof'] === REACT_ELEMENT_TYPE;
      return el === undefined || el === null || typeof el === 'boolean' || typeof el === 'string' || isValidElement;
    };

    var adornment = (propName === 'startAdornment' || propName === 'endAdornment') && props[propName];

    if (!isReactNodeType(adornment)) {
      var name = adornment.displayName || adornment.name || 'Component';

      if (!(name.indexOf('Icon') === 0)) {
        return new Error("Invalid prop \"".concat(propName, "\". Invalid React component with name \"").concat(name, "\" was supplied to \"").concat(componentName, "\". Only Icon components are allowed.\n"));
      }
    }
  },
  "value": _PropTypes.string,
  "variant": _PropTypes.oneOf(["default", "error", "info", "success", "warning"])
};
Tag.displayName = "Tag";

export { Tag };
